import { supabase } from '../supabase-client';
import type { Database } from '../types/database';

type Document = Database['public']['Tables']['documents']['Row'];
type DocumentInsert = Database['public']['Tables']['documents']['Insert'];
type DocumentUpdate = Database['public']['Tables']['documents']['Update'];

// Get all documents for a user
export const getUserDocuments = async (userId: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Document[];
};

// Get a single document
export const getDocument = async (documentId: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (error) throw error;
  return data as Document;
};

// Create a new document
export const createDocument = async (document: DocumentInsert) => {
  const { data, error } = await supabase
    .from('documents')
    .insert(document)
    .select()
    .single();

  if (error) throw error;
  return data as Document;
};

// Update a document
export const updateDocument = async (documentId: string, updates: DocumentUpdate) => {
  const { data, error } = await supabase
    .from('documents')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', documentId)
    .select()
    .single();

  if (error) throw error;
  return data as Document;
};

// Delete a document
export const deleteDocument = async (documentId: string) => {
  const { error } = await supabase
    .from('documents')
    .delete()
    .eq('id', documentId);

  if (error) throw error;
};

// Get documents by country
export const getDocumentsByCountry = async (userId: string, country: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('user_id', userId)
    .eq('country', country)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Document[];
};

// Get documents by type
export const getDocumentsByType = async (userId: string, documentType: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('user_id', userId)
    .eq('document_type', documentType)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Document[];
};

// Get draft documents for a user
export const getDraftDocuments = async (userId: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'draft')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data as Document[];
};

// Get document count for a user
export const getDocumentCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('documents')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (error) throw error;
  return count || 0;
};

// Get document count for current month
export const getDocumentCountThisMonth = async (userId: string): Promise<number> => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  
  const { count, error } = await supabase
    .from('documents')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', startOfMonth);

  if (error) throw error;
  return count || 0;
};

